import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';

const EMAIL_TYPES = ['personal', 'work', 'other'];
const PHONE_TYPES = ['mobile', 'home', 'work', 'other'];

function ContactForm({ contact, onClose, onSave }) {
  const navigate = useNavigate();
  const { tags, fetchTags, categories, fetchCategories, checkDuplicateContact, updateContact } = useApp();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    emails: [{ value: '', type: 'personal' }],
    phones: [{ value: '', type: 'mobile' }],
    company: '',
    job_title: '',
    linkedin_url: '',
    linkedin_connected_date: '',
    twitter_handle: '',
    address: '',
    birthday: '',
    how_we_met: '',
    interests: '',
    notes: '',
    relationship_context: '',
    category: 'acquaintance',
    relationship_strength: 50,
    contact_frequency_goal: 30,
    tags: []
  });
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Fetch tags and categories once on mount
  useEffect(() => {
    fetchTags();
    fetchCategories();
  }, []); // Only fetch once on mount

  useEffect(() => {
    if (contact) {
      // Convert legacy single email/phone to array format with defensive handling
      const normalizeEmails = (c) => {
        if (Array.isArray(c.emails) && c.emails.length > 0) {
          return c.emails.map(e => typeof e === 'string' ? { value: e, type: 'personal' } : e);
        }
        if (c.email) return [{ value: c.email, type: 'personal' }];
        return [{ value: '', type: 'personal' }];
      };

      const normalizePhones = (c) => {
        if (Array.isArray(c.phones) && c.phones.length > 0) {
          return c.phones.map(p => typeof p === 'string' ? { value: p, type: 'mobile' } : p);
        }
        if (c.phone) return [{ value: c.phone, type: 'mobile' }];
        return [{ value: '', type: 'mobile' }];
      };

      // Handle tags - they might be objects with id or just string IDs
      const tagIds = Array.isArray(contact.tags)
        ? contact.tags.map(t => typeof t === 'string' ? t : t?.id).filter(Boolean)
        : [];

      setFormData({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        emails: normalizeEmails(contact),
        phones: normalizePhones(contact),
        company: contact.company || '',
        job_title: contact.job_title || '',
        linkedin_url: contact.linkedin_url || '',
        linkedin_connected_date: contact.linkedin_connected_date || '',
        twitter_handle: contact.twitter_handle || '',
        address: contact.address || '',
        birthday: contact.birthday || '',
        how_we_met: contact.how_we_met || '',
        interests: contact.interests || '',
        notes: contact.notes || '',
        relationship_context: contact.relationship_context || '',
        category: contact.category || 'acquaintance',
        relationship_strength: contact.relationship_strength || 50,
        contact_frequency_goal: contact.contact_frequency_goal || 30,
        tags: tagIds
      });
    }
  }, [contact]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
    setDuplicateWarning(null);
  };

  const handleEmailChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.map((email, i) =>
        i === index ? { ...email, [field]: value } : email
      )
    }));
    setDuplicateWarning(null);
  };

  const handlePhoneChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.map((phone, i) =>
        i === index ? { ...phone, [field]: value } : phone
      )
    }));
  };

  const addEmail = () => {
    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, { value: '', type: 'personal' }]
    }));
  };

  const removeEmail = (index) => {
    if (formData.emails.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  const addPhone = () => {
    setFormData(prev => ({
      ...prev,
      phones: [...prev.phones, { value: '', type: 'mobile' }]
    }));
  };

  const removePhone = (index) => {
    if (formData.phones.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.filter((_, i) => i !== index)
    }));
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim()) return;

    setSaving(true);
    try {
      // Check for duplicates only when creating new contact
      if (!contact && !duplicateWarning) {
        // Prepare data with primary email for duplicate check
        const checkData = {
          ...formData,
          email: formData.emails[0]?.value || ''
        };
        const duplicate = await checkDuplicateContact(checkData);

        if (duplicate) {
          setDuplicateWarning(duplicate);
          setSaving(false);
          return;
        }
      }

      // Clean up empty emails/phones (handle both object and string formats)
      const cleanedEmails = formData.emails.filter(e => {
        const val = typeof e === 'string' ? e : e?.value;
        return val && val.trim();
      }).map(e => typeof e === 'string' ? { value: e, type: 'personal' } : e);

      const cleanedPhones = formData.phones.filter(p => {
        const val = typeof p === 'string' ? p : p?.value;
        return val && val.trim();
      }).map(p => typeof p === 'string' ? { value: p, type: 'mobile' } : p);

      const cleanedData = {
        ...formData,
        emails: cleanedEmails,
        phones: cleanedPhones,
        // Keep primary email/phone for backwards compatibility
        email: cleanedEmails[0]?.value || '',
        phone: cleanedPhones[0]?.value || ''
      };

      await onSave(cleanedData);
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleMerge = async () => {
    if (!duplicateWarning) return;

    setSaving(true);
    try {
      // Merge: update existing contact with new data (only fill empty fields)
      const existing = duplicateWarning.existingContact;
      const mergeData = {};

      // Only add fields that are empty in existing but present in new data
      Object.keys(formData).forEach(key => {
        if (key === 'emails' || key === 'phones' || key === 'tags') return;
        if (formData[key] && !existing[key]) {
          mergeData[key] = formData[key];
        }
      });

      // Merge emails and phones
      const existingEmails = existing.emails || (existing.email ? [{ value: existing.email, type: 'personal' }] : []);
      const newEmails = formData.emails.filter(e => e.value.trim());
      const mergedEmails = [...existingEmails];
      newEmails.forEach(newEmail => {
        if (!mergedEmails.some(e => e.value.toLowerCase() === newEmail.value.toLowerCase())) {
          mergedEmails.push(newEmail);
        }
      });
      if (mergedEmails.length > existingEmails.length) {
        mergeData.emails = mergedEmails;
        mergeData.email = mergedEmails[0]?.value || '';
      }

      const existingPhones = existing.phones || (existing.phone ? [{ value: existing.phone, type: 'mobile' }] : []);
      const newPhones = formData.phones.filter(p => p.value.trim());
      const mergedPhones = [...existingPhones];
      newPhones.forEach(newPhone => {
        if (!mergedPhones.some(p => p.value === newPhone.value)) {
          mergedPhones.push(newPhone);
        }
      });
      if (mergedPhones.length > existingPhones.length) {
        mergeData.phones = mergedPhones;
        mergeData.phone = mergedPhones[0]?.value || '';
      }

      await updateContact(existing.id, mergeData);
      onClose();
      navigate(`/contacts/${existing.id}`);
    } catch (error) {
      console.error('Error merging contact:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAnyway = async () => {
    setDuplicateWarning(null);
    setSaving(true);
    try {
      const cleanedData = {
        ...formData,
        emails: formData.emails.filter(e => e.value.trim()),
        phones: formData.phones.filter(p => p.value.trim()),
        email: formData.emails[0]?.value || '',
        phone: formData.phones[0]?.value || ''
      };
      await onSave(cleanedData);
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setSaving(false);
    }
  };

  const getStrengthColor = (strength) => {
    if (strength >= 70) return 'var(--success)';
    if (strength >= 40) return 'var(--warning)';
    return 'var(--danger)';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {contact ? 'Edit Contact' : 'Add Contact'}
          </h2>
          <button className="btn btn-ghost" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Duplicate Warning */}
        {duplicateWarning && (
          <div style={{
            padding: '1rem',
            background: 'rgba(245, 158, 11, 0.1)',
            borderBottom: '1px solid var(--warning)'
          }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <AlertTriangle size={24} style={{ color: 'var(--warning)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div className="font-medium" style={{ marginBottom: '0.5rem' }}>
                  Possible Duplicate Found
                </div>
                <div className="text-sm" style={{ marginBottom: '0.75rem' }}>
                  A contact with the same {duplicateWarning.type === 'email' ? 'email' : 'name'} already exists:
                  <strong style={{ marginLeft: '0.5rem' }}>
                    {duplicateWarning.existingContact.first_name} {duplicateWarning.existingContact.last_name}
                  </strong>
                  {duplicateWarning.existingContact.email && (
                    <span className="text-muted"> ({duplicateWarning.existingContact.email})</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-secondary btn-sm" onClick={handleMerge} disabled={saving}>
                    Merge with Existing
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={handleCreateAnyway} disabled={saving}>
                    Create Anyway
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setDuplicateWarning(null)}>
                    Edit Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Basic Info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
                Basic Information
              </h3>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Multiple Emails */}
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label className="form-label" style={{ marginBottom: 0 }}>Email</label>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={addEmail}>
                    <Plus size={14} /> Add
                  </button>
                </div>
                {formData.emails.map((email, index) => (
                  <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="email"
                      value={email.value}
                      onChange={(e) => handleEmailChange(index, 'value', e.target.value)}
                      className="form-input"
                      style={{ flex: 1 }}
                      placeholder="email@example.com"
                    />
                    <select
                      value={email.type}
                      onChange={(e) => handleEmailChange(index, 'type', e.target.value)}
                      className="form-input form-select"
                      style={{ width: '100px' }}
                    >
                      {EMAIL_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {formData.emails.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => removeEmail(index)}
                        style={{ color: 'var(--danger)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Multiple Phones */}
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label className="form-label" style={{ marginBottom: 0 }}>Phone</label>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={addPhone}>
                    <Plus size={14} /> Add
                  </button>
                </div>
                {formData.phones.map((phone, index) => (
                  <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="tel"
                      value={phone.value}
                      onChange={(e) => handlePhoneChange(index, 'value', e.target.value)}
                      className="form-input"
                      style={{ flex: 1 }}
                      placeholder="555-0100"
                    />
                    <select
                      value={phone.type}
                      onChange={(e) => handlePhoneChange(index, 'type', e.target.value)}
                      className="form-input form-select"
                      style={{ width: '100px' }}
                    >
                      {PHONE_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {formData.phones.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => removePhone(index)}
                        style={{ color: 'var(--danger)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Professional Info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
                Professional
              </h3>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Job Title</label>
                  <input
                    type="text"
                    name="job_title"
                    value={formData.job_title}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">LinkedIn URL</label>
                <input
                  type="url"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">LinkedIn Connected Since</label>
                  <input
                    type="date"
                    name="linkedin_connected_date"
                    value={formData.linkedin_connected_date}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Twitter Handle</label>
                  <input
                    type="text"
                    name="twitter_handle"
                    value={formData.twitter_handle}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="@username"
                  />
                </div>
              </div>
            </div>

            {/* Relationship Info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
                Relationship
              </h3>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="form-input form-select"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name.toLowerCase()}>
                        {cat.name}
                      </option>
                    ))}
                    {categories.length === 0 && (
                      <>
                        <option value="family">Family</option>
                        <option value="friends">Friends</option>
                        <option value="professional">Professional</option>
                        <option value="acquaintance">Acquaintance</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Every (days)</label>
                  <input
                    type="number"
                    name="contact_frequency_goal"
                    value={formData.contact_frequency_goal}
                    onChange={handleChange}
                    className="form-input"
                    min="1"
                    max="365"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  Relationship Strength: {formData.relationship_strength}%
                </label>
                <input
                  type="range"
                  name="relationship_strength"
                  value={formData.relationship_strength}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  style={{ width: '100%' }}
                />
                <div className="strength-bar" style={{ marginTop: '0.5rem' }}>
                  <div
                    className="strength-fill"
                    style={{
                      width: `${formData.relationship_strength}%`,
                      background: getStrengthColor(formData.relationship_strength)
                    }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">How We Met</label>
                <input
                  type="text"
                  name="how_we_met"
                  value={formData.how_we_met}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Conference, mutual friend, online..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Relationship Context</label>
                <textarea
                  name="relationship_context"
                  value={formData.relationship_context}
                  onChange={handleChange}
                  className="form-input form-textarea"
                  placeholder="Context about your relationship..."
                />
              </div>
            </div>

            {/* Additional Info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
                Additional Details
              </h3>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Birthday</label>
                  <input
                    type="date"
                    name="birthday"
                    value={formData.birthday}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Interests</label>
                <input
                  type="text"
                  name="interests"
                  value={formData.interests}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Hobbies, topics they care about..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="form-input form-textarea"
                  placeholder="Any other notes..."
                />
              </div>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-700)' }}>
                  Tags
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {tags.map(tag => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => handleTagToggle(tag.id)}
                      style={{
                        padding: '0.375rem 0.75rem',
                        borderRadius: '9999px',
                        border: '1px solid',
                        borderColor: formData.tags.includes(tag.id) ? tag.color : 'var(--gray-300)',
                        background: formData.tags.includes(tag.id) ? tag.color : 'transparent',
                        color: formData.tags.includes(tag.id) ? 'white' : 'var(--gray-600)',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : contact ? 'Update Contact' : 'Add Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactForm;
